import { useMemo } from "react";

import { Button } from "@/components/ui/button";

import { GroupCard } from "../components/GroupCard";
import type { BlockingError } from "../state/selectors";
import { getDuplicateCustomerAssignments } from "../state/selectors";
import type {
  CustomerMinimal,
  FileEntry,
  Group,
  WizardData,
} from "../state/types";

type PhaseReviewSuggestedProps = {
  data: WizardData;
  customers: CustomerMinimal[];
  blocking_errors: BlockingError[];

  onSelectCustomer: (groupId: string, customerId: string | null) => void;
  onApproveGroup: (groupId: string) => void;
  onMarkNeedsEdit: (groupId: string) => void;
  onContinue: () => void;
};

/**
 * One-line summary string for a blocking error, used in the inline
 * blocker panel.
 */
function describeBlockingError(error: BlockingError): string {
  switch (error.kind) {
    case "no_period":
      return "Choose a period.";
    case "no_files":
      return "Select at least one PDF.";
    case "suggested_group_unresolved":
      return "A suggested group still needs Approve or Needs-manual-edit.";
    case "ungrouped_files_remain":
      return `${error.count} file${error.count === 1 ? "" : "s"} still ungrouped.`;
    case "group_missing_customer":
      return "A non-empty group has no selected customer.";
    case "duplicate_customer_assignment":
      return `Customer is assigned to ${error.group_ids.length} groups.`;
    case "empty_group_included":
      return "An empty group is present in state.";
    case "file_in_multiple_locations":
      return "A file appears in more than one group.";
    case "file_missing_from_all_locations":
      return "A file is missing from every group and ungrouped.";
    default: {
      const _exhaustive: never = error;
      void _exhaustive;
      return "Unknown blocking error.";
    }
  }
}

/**
 * Phase 2 — Review suggested groups.
 *
 * Renders bucket 1 (High confidence) and bucket 2 (Plausible) suggested
 * groups in two visual sections. Each card lets the operator approve,
 * mark as needs-edit, or change the selected customer. The Approve and
 * Needs-manual-edit buttons stay visible regardless of current state so
 * the user can flip freely between the two.
 *
 * Per spec, manual groups don't exist yet at Phase 2 (they're only
 * created in Phase 3 from bucket-3 / ungrouped files). If one somehow
 * does, it is skipped here.
 *
 * Continue is enabled only when no blocking errors remain for this phase.
 */
export function PhaseReviewSuggested({
  data,
  customers,
  blocking_errors,
  onSelectCustomer,
  onApproveGroup,
  onMarkNeedsEdit,
  onContinue,
}: PhaseReviewSuggestedProps) {
  // Pre-compute which group ids participate in a duplicate-customer
  // collision so we can surface an inline warning on each affected
  // card's CustomerPicker.
  const duplicateGroupIds = useMemo((): Set<string> => {
    const out = new Set<string>();
    for (const dup of getDuplicateCustomerAssignments(data)) {
      for (const gid of dup.group_ids) out.add(gid);
    }
    return out;
  }, [data]);

  // Partition suggested-source groups by bucket. Manual-source groups
  // are not expected at Phase 2 and are intentionally not rendered.
  const { bucket1Groups, bucket2Groups } = useMemo(() => {
    const b1: Group[] = [];
    const b2: Group[] = [];
    for (const g of data.groups) {
      if (g.source !== "suggested") continue;
      if (g.initial_bucket === 1) b1.push(g);
      else if (g.initial_bucket === 2) b2.push(g);
    }
    // Sort by file count desc within each section so the most
    // consequential groups come first.
    const byFileCountDesc = (a: Group, b: Group): number =>
      b.file_ids.length - a.file_ids.length;
    b1.sort(byFileCountDesc);
    b2.sort(byFileCountDesc);
    return { bucket1Groups: b1, bucket2Groups: b2 };
  }, [data.groups]);

  const ungroupedCount = data.ungrouped_file_ids.length;
  const totalSuggestedGroups = bucket1Groups.length + bucket2Groups.length;
  const canContinue = blocking_errors.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">Review suggested groups</h2>
        <p className="text-sm text-muted-foreground">
          Each card groups PDFs the matcher believes belong to the same
          customer. Approve groups that look right, or mark them as
          needing manual edits. You can change the selected customer at
          any time using the suggestions or the full customer list.
        </p>
        <p className="text-xs text-muted-foreground">
          {totalSuggestedGroups} suggested group
          {totalSuggestedGroups === 1 ? "" : "s"} · {ungroupedCount} file
          {ungroupedCount === 1 ? "" : "s"} will need manual grouping next.
        </p>
      </div>

      {totalSuggestedGroups === 0 ? (
        <div className="rounded border border-dashed border-muted-foreground/40 bg-muted/20 p-6 text-sm text-muted-foreground">
          No high-confidence or plausible suggestions were found. Continue
          to manual grouping to assign all {ungroupedCount} file
          {ungroupedCount === 1 ? "" : "s"} by hand.
        </div>
      ) : null}

      {bucket1Groups.length > 0 ? (
        <Section
          title="High confidence"
          subtitle="The matcher is very confident about these. Approve to lock them in."
          groups={bucket1Groups}
          data={data}
          customers={customers}
          duplicateGroupIds={duplicateGroupIds}
          onSelectCustomer={onSelectCustomer}
          onApproveGroup={onApproveGroup}
          onMarkNeedsEdit={onMarkNeedsEdit}
        />
      ) : null}

      {bucket2Groups.length > 0 ? (
        <Section
          title="Plausible matches"
          subtitle="Worth a closer look — the runner-up customer was close."
          groups={bucket2Groups}
          data={data}
          customers={customers}
          duplicateGroupIds={duplicateGroupIds}
          onSelectCustomer={onSelectCustomer}
          onApproveGroup={onApproveGroup}
          onMarkNeedsEdit={onMarkNeedsEdit}
        />
      ) : null}

      {blocking_errors.length > 0 ? (
        <div className="rounded border border-muted bg-muted/30 p-2 text-xs text-muted-foreground flex flex-col gap-0.5">
          {blocking_errors.map((e: BlockingError, idx: number) => (
            <span key={`${e.kind}-${idx}`}>• {describeBlockingError(e)}</span>
          ))}
        </div>
      ) : null}

      <div className="flex flex-row justify-end">
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue to manual grouping
        </Button>
      </div>
    </div>
  );
}

type SectionProps = {
  title: string;
  subtitle: string;
  groups: Group[];
  data: WizardData;
  customers: CustomerMinimal[];
  duplicateGroupIds: Set<string>;
  onSelectCustomer: (groupId: string, customerId: string | null) => void;
  onApproveGroup: (groupId: string) => void;
  onMarkNeedsEdit: (groupId: string) => void;
};

function Section({
  title,
  subtitle,
  groups,
  data,
  customers,
  duplicateGroupIds,
  onSelectCustomer,
  onApproveGroup,
  onMarkNeedsEdit,
}: SectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {groups.map((group: Group) => {
          const dupWarning = duplicateGroupIds.has(group.id)
            ? "This customer is selected in another group. Resolve the duplicate before continuing."
            : undefined;
          return (
            <GroupCard
              key={group.id}
              group={group}
              files={data.files as Record<string, FileEntry>}
              customers={customers}
              showCustomerPicker
              customerPickerError={dupWarning}
              onCustomerChange={(customerId: string | null) =>
                onSelectCustomer(group.id, customerId)
              }
              onApprove={() => onApproveGroup(group.id)}
              onMarkNeedsEdit={() => onMarkNeedsEdit(group.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
